<template>
    <div
        :class="[
            hasErrorStartingWith(errors, 'shipping')
                ? 'border-red-600'
                : 'border-gray-200',
            'mt-4 mb-6 lg:mb-4 bg-white shadow rounded-lg border',
        ]"
    >
        <div
            :class="[
                hasErrorStartingWith(errors, 'shipping')
                    ? 'bg-red-500 text-white border-red-600'
                    : 'bg-gray-100 text-gray-800 border-gray-200',
                'flex items-center justify-between px-4 py-5 sm:px-6 rounded-t-lg border-b',
            ]"
        >
            <div class="flex items-center">
                <TruckIcon class="h-5 w-5 mr-2" aria-hidden="true" />
                <h2 class="font-bold">Shipping Address</h2>
            </div>
        </div>

        <div class="md:grid md:grid-cols-2 md:gap-6 px-4 py-5 sm:px-6">
            <div class="mt-0 md:col-span-2">
                <div class="grid grid-cols-6 gap-6">
                    <div class="col-span-6 sm:col-span-4">
                        <label
                            for="shipping_name"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Full Name
                        </label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input
                                v-model="inputs.name"
                                :ref="refs.name"
                                id="shipping_name"
                                name="shipping_name"
                                type="text"
                                autocomplete="shipping name"
                                required
                                :class="[
                                    hasError(errors, 'shipping_name')
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="
                                    hasError(errors, 'shipping_name')
                                        ? 'true'
                                        : 'false'
                                "
                                :aria-describedby="
                                    hasError(errors, 'shipping_name')
                                        ? 'shipping_name-error'
                                        : ''
                                "
                            />
                            <div
                                v-if="hasError(errors, 'shipping_name')"
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <ExclamationCircleIcon
                                    class="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <p
                            v-if="hasError(errors, 'shipping_name')"
                            class="mt-2 text-sm text-red-600"
                            id="shipping_name-error"
                        >
                            {{ error(errors, "shipping_name") }}
                        </p>
                    </div>

                    <div class="col-span-6">
                        <label
                            for="shipping_line1"
                            class="block text-sm font-medium text-gray-700"
                        >
                            {{ ucwords(country.line1) }}
                        </label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input
                                v-model="inputs.line1"
                                :ref="refs.line1"
                                @placeChanged="onPlaceChanged"
                                @keydown.enter="onEnterKeyDown"
                                @change="onAddressChanged"
                                name="shipping_line1"
                                id="shipping_line1"
                                type="text"
                                required
                                placeholder=""
                                autocomplete="shipping address-line1"
                                :class="[
                                    hasError(errors, 'shipping_line1')
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    verificationErrors.line1 ? 'pr-10' : '',
                                    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="
                                    hasError(errors, 'shipping_line1')
                                        ? 'true'
                                        : 'false'
                                "
                                :aria-describedby="
                                    hasError(errors, 'shipping_line1')
                                        ? 'shipping_line1-error'
                                        : ''
                                "
                            />
                            <div
                                v-if="
                                    hasError(errors, 'shipping_line1') &&
                                    !verificationErrors.line1
                                "
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <ExclamationCircleIcon
                                    class="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                />
                            </div>
                            <div
                                v-if="verificationErrors.line1"
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <QuestionMarkCircleIcon
                                    class="h-5 w-5 text-amber-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <p
                            v-if="hasError(errors, 'shipping_line1')"
                            class="mt-2 text-sm text-red-600"
                            id="shipping_line1-error"
                        >
                            {{ error(errors, "shipping_line1") }}
                        </p>
                        <p
                            v-if="verificationErrors.line1"
                            class="mt-2 text-sm text-amber-600"
                            id="shipping_line1-verification-error"
                        >
                            {{ verificationErrors.line1 }}
                        </p>
                    </div>

                    <div class="col-span-6">
                        <label
                            for="shipping_line2"
                            class="block text-sm font-medium text-gray-700"
                        >
                            {{ ucwords(country.line2) }}
                        </label>

                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input
                                v-model="inputs.line2"
                                :ref="refs.line2"
                                @change="onAddressChanged"
                                name="shipping_line2"
                                id="shipping_line2"
                                type="text"
                                autocomplete="shipping address-line2"
                                placeholder="(optional)"
                                :class="[
                                    hasError(errors, 'shipping_line2')
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    verificationErrors.line2 ? 'pr-10' : '',
                                    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="
                                    hasError(errors, 'shipping_line2')
                                        ? 'true'
                                        : 'false'
                                "
                                :aria-describedby="
                                    hasError(errors, 'shipping_line2')
                                        ? 'shipping_line2-error'
                                        : ''
                                "
                            />
                            <div
                                v-if="
                                    hasError(errors, 'shipping_line2') &&
                                    !verificationErrors.line2
                                "
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <ExclamationCircleIcon
                                    class="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                />
                            </div>
                            <div
                                v-if="verificationErrors.line2"
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <QuestionMarkCircleIcon
                                    class="h-5 w-5 text-amber-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <p
                            v-if="hasError(errors, 'shipping_line2')"
                            class="mt-2 text-sm text-red-600"
                            id="shipping_line2-error"
                        >
                            {{ error(errors, "shipping_line2") }}
                        </p>
                        <p
                            v-if="verificationErrors.line2"
                            class="mt-2 text-sm text-amber-600"
                            id="shipping_line2-verification-error"
                        >
                            {{ verificationErrors.line2 }}
                        </p>
                    </div>

                    <div
                        v-if="country.city"
                        class="col-span-6 sm:col-span-6 lg:col-span-3"
                    >
                        <label
                            for="shipping_city"
                            class="block text-sm font-medium text-gray-700"
                        >
                            {{ ucwords(country.city) }}
                        </label>

                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input
                                v-model="inputs.city"
                                :ref="refs.city"
                                @change="onAddressChanged()"
                                name="shipping_city"
                                id="shipping_city"
                                type="text"
                                autocomplete="shipping address-level2"
                                required
                                :class="[
                                    shippingRegionErrors.city ||
                                    hasError(errors, 'shipping_city')
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="
                                    shippingRegionErrors.city ||
                                    hasError(errors, 'shipping_city')
                                        ? 'true'
                                        : 'false'
                                "
                                :aria-describedby="
                                    shippingRegionErrors.city ||
                                    hasError(errors, 'shipping_city')
                                        ? 'shipping_city-error'
                                        : ''
                                "
                            />
                            <div
                                v-if="
                                    shippingRegionErrors.city ||
                                    hasError(errors, 'shipping_city')
                                "
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <ExclamationCircleIcon
                                    class="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <p
                            v-if="
                                shippingRegionErrors.city ||
                                hasError(errors, 'shipping_city')
                            "
                            class="mt-2 text-sm text-red-600"
                            id="shipping_city-error"
                        >
                            {{
                                shippingRegionErrors.city
                                    ? shippingRegionErrors.city
                                    : error(errors, "shipping_city")
                            }}
                        </p>
                    </div>

                    <div v-if="country.state" class="col-span-6 sm:col-span-3">
                        <label
                            for="shipping_state"
                            class="block text-sm font-medium text-gray-700"
                        >
                            {{ ucwords(country.state) }}
                        </label>

                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input
                                v-if="isEmpty(states)"
                                v-model="inputs.state"
                                :ref="refs.state"
                                @change="onAddressChanged()"
                                name="shipping_state"
                                id="shipping_state"
                                type="text"
                                autocomplete="shipping address-level1"
                                required
                                :class="[
                                    hasError(errors, 'shipping_state')
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="
                                    hasError(errors, 'shipping_state')
                                        ? 'true'
                                        : 'false'
                                "
                                :aria-describedby="
                                    hasError(errors, 'shipping_state')
                                        ? 'shipping_state-error'
                                        : ''
                                "
                            />
                            <select
                                v-else
                                v-model="inputs.state"
                                :ref="refs.state"
                                @change="onAddressChanged"
                                id="shipping_state"
                                name="shipping_state"
                                autocomplete="shipping address-level1"
                                required
                                :class="[
                                    hasError(errors, 'shipping_state')
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-14'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="
                                    hasError(errors, 'shipping_state')
                                        ? 'true'
                                        : 'false'
                                "
                                :aria-describedby="
                                    hasError(errors, 'shipping_state')
                                        ? 'shipping_state-error'
                                        : ''
                                "
                            >
                                <option value="" disabled hidden selected>
                                    --- Select a
                                    {{ ucwords(country.state) }} ---
                                </option>
                                <option
                                    v-for="(state, code) in states"
                                    :key="code"
                                    :value="code"
                                >
                                    {{ state }}
                                </option>
                            </select>
                            <div
                                v-if="hasError(errors, 'shipping_state')"
                                :class="[
                                    isEmpty(states) ? 'pr-3' : 'pr-8',
                                    'absolute inset-y-0 right-0 flex items-center pointer-events-none',
                                ]"
                            >
                                <ExclamationCircleIcon
                                    class="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <p
                            v-if="hasError(errors, 'shipping_state')"
                            class="mt-2 text-sm text-red-600"
                            id="shipping_state-error"
                        >
                            {{ error(errors, "shipping_state") }}
                        </p>
                    </div>

                    <div
                        v-if="country.postal_code"
                        class="col-span-6 sm:col-span-3 lg:col-span-2"
                    >
                        <label
                            for="shipping_postal_code"
                            class="block text-sm font-medium text-gray-700"
                        >
                            {{ ucwords(country.postal_code) }}
                        </label>

                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input
                                v-model="inputs.postal_code"
                                :ref="refs.postal_code"
                                @change="onAddressChanged()"
                                name="shipping_postal_code"
                                id="shipping_postal_code"
                                type="text"
                                autocomplete="shipping postal-code"
                                :required="
                                    country.shipping_region_field ===
                                    'postal_code'
                                "
                                :class="[
                                    shippingRegionErrors.postal_code ||
                                    hasError(errors, 'shipping_postal_code')
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    verificationErrors.postal_code
                                        ? 'pr-10'
                                        : '',
                                    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="
                                    shippingRegionErrors.postal_code ||
                                    hasError(errors, 'shipping_postal_code')
                                        ? 'true'
                                        : 'false'
                                "
                                :aria-describedby="
                                    shippingRegionErrors.postal_code ||
                                    hasError(errors, 'shipping_postal_code')
                                        ? 'shipping_postal_code-error'
                                        : ''
                                "
                            />
                            <div
                                v-if="
                                    (shippingRegionErrors.postal_code ||
                                        hasError(
                                            errors,
                                            'shipping_postal_code',
                                        )) &&
                                    !verificationErrors.postal_code
                                "
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <ExclamationCircleIcon
                                    class="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                />
                            </div>
                            <div
                                v-if="verificationErrors.postal_code"
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <QuestionMarkCircleIcon
                                    class="h-5 w-5 text-amber-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <p
                            v-if="
                                shippingRegionErrors.postal_code ||
                                hasError(errors, 'shipping_postal_code')
                            "
                            class="mt-2 text-sm text-red-600"
                            id="shipping_postal_code-error"
                        >
                            {{
                                shippingRegionErrors.postal_code
                                    ? shippingRegionErrors.postal_code
                                    : error(errors, "shipping_postal_code")
                            }}
                        </p>
                        <p
                            v-if="verificationErrors.postal_code"
                            class="mt-2 text-sm text-amber-600"
                            id="shipping_postal_code-verification-error"
                        >
                            {{ verificationErrors.postal_code }}
                        </p>
                    </div>

                    <div class="col-span-6 sm:col-span-4">
                        <label
                            for="shipping_country_code"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Country
                        </label>

                        <div class="mt-1 relative rounded-md shadow-sm">
                            <select
                                v-model="inputs.country_code"
                                :ref="refs.country_code"
                                @change="updateCountry"
                                id="shipping_country_code"
                                name="shipping_country_code"
                                autocomplete="shipping country"
                                required
                                :class="[
                                    hasError(errors, 'shipping_country_code')
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-14'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="
                                    hasError(errors, 'shipping_country_code')
                                        ? 'true'
                                        : 'false'
                                "
                                :aria-describedby="
                                    hasError(errors, 'shipping_country_code')
                                        ? 'shipping_country_code-error'
                                        : ''
                                "
                            >
                                <option
                                    v-for="(country, code) in countries"
                                    :key="code"
                                    :value="code"
                                >
                                    {{ country }}
                                </option>
                            </select>
                            <div
                                v-if="hasError(errors, 'shipping_country_code')"
                                class="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none"
                            >
                                <ExclamationCircleIcon
                                    class="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <p
                            v-if="hasError(errors, 'shipping_country_code')"
                            class="mt-2 text-sm text-red-600"
                            id="shipping_country_code-error"
                        >
                            {{ error(errors, "shipping_country_code") }}
                        </p>
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                        <label
                            for="shipping_phone"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Phone
                        </label>

                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input
                                v-model="inputs.phone"
                                :ref="refs.phone"
                                @focus="onAddressChanged"
                                name="shipping_phone"
                                id="shipping_phone"
                                type="tel"
                                autocomplete="phone"
                                placeholder="(optional)"
                                :class="[
                                    hasError(errors, 'shipping_phone')
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    'appearance-none block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="
                                    hasError(errors, 'shipping_phone')
                                        ? 'true'
                                        : 'false'
                                "
                                :aria-describedby="
                                    hasError(errors, 'shipping_phone')
                                        ? 'shipping_phone-error'
                                        : ''
                                "
                            />
                            <div
                                v-if="hasError(errors, 'shipping_phone')"
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <ExclamationCircleIcon
                                    class="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                />
                            </div>
                            <InputTooltip
                                v-else
                                message="In case we need to contact you about your order"
                            />
                        </div>
                        <p
                            v-if="hasError(errors, 'shipping_phone')"
                            class="mt-2 text-sm text-red-600"
                            id="shipping_phone-error"
                        >
                            {{ error(errors, "shipping_phone") }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { inject, onMounted, reactive, ref } from "vue";
import type { Ref } from "vue";
import {
    ExclamationCircleIcon,
    TruckIcon,
    QuestionMarkCircleIcon,
} from "@heroicons/vue/solid";
import { useForm } from "../../functions/useForm";
import { useShippingOption } from "../../functions/useShippingOption";
import { useGoogleMapsAutocomplete } from "../../functions/useGoogleMapsAutocomplete";
import { useString } from "../../functions/useString";
import { useVerifyAddress } from "../../functions/useVerifyAddress";
import { isEmpty } from "lodash";
import InputTooltip from "../InputTooltip.vue";
import { updateCountry as updateCountryRequest } from "../../api/countries";
import type { AppEmitter } from "../../emitter";

const props = defineProps<{
    countries?: any;
    errors?: any;
    initialCountry?: any;
    initialStates?: any;
    old?: any;
}>();

const emitter = inject<AppEmitter>("mitt")!;

const { error, hasError, hasErrorStartingWith, oldInput } = useForm();
const {
    initializeAutocomplete,
    onEnterKeyDown,
    pasteAutocompletedAddress,
    restrictAutocompleteByCountry,
} = useGoogleMapsAutocomplete();
const { updateShippingRegion } = useShippingOption();
const { ucwords } = useString();
const { verificationErrors, verifyAddress } = useVerifyAddress();

const country = ref(props.initialCountry);
const states = ref(props.initialStates);
const oldCountryCode = oldInput(props.old, "shipping_country_code");

if (oldCountryCode && oldCountryCode !== country.value.code) {
    updateCountry(oldCountryCode);
}

const refs: Record<string, Ref<any>> = {
    name: ref(null),
    line1: ref(null),
    line2: ref(null),
    city: ref(null),
    state: ref(null),
    postal_code: ref(null),
    country_code: ref(null),
    phone: ref(null),
};

const inputs = reactive<Record<string, any>>({
    name: oldInput(props.old, "shipping_name"),
    line1: oldInput(props.old, "shipping_line1"),
    line2: oldInput(props.old, "shipping_line2"),
    city: oldInput(props.old, "shipping_city"),
    state: oldInput(props.old, "shipping_state")
        ? oldInput(props.old, "shipping_state")
        : "",
    postal_code: oldInput(props.old, "shipping_postal_code"),
    country_code: country.value.code,
    phone: oldInput(props.old, "shipping_phone"),
});

const shippingRegionErrors = reactive<Record<string, string>>({
    city: "",
    postal_code: "",
});

function copyShippingAddressFields() {
    emitter.emit("copiedShippingAddressFields", inputs);
}

function hasRequiredAddressFields() {
    return !!(
        (inputs.line1 || inputs.line2) &&
        inputs.city &&
        inputs.state &&
        inputs.postal_code
    );
}

function onPlaceChanged() {
    refs.line1.value.value = inputs.line1;
    pasteAutocompletedAddress(inputs);
    onAddressChanged();
}

// `countryCode` is a string when called from script, but the `@change` binding in
// the template invokes this bare, passing the DOM Event; in that case we fall back
// to the current `inputs.country_code` (matching the prior untyped behavior).
function updateCountry(countryCode?: string | Event) {
    const code =
        typeof countryCode === "string" && countryCode.length === 2
            ? countryCode
            : inputs.country_code;

    updateCountryRequest({
        country_code: code,
    }).then((data) => {
        inputs.country_code = data.country.code;
        country.value = data.country;
        states.value = data.states;
        updateShippingOptions();
        restrictAutocompleteByCountry(country.value.code);
    });
}

function updateShippingOptions() {
    if (!hasRequiredAddressFields()) {
        return;
    }

    updateShippingRegion({
        country_code: inputs.country_code,
        region: inputs[country.value.shipping_region_field],
        line1: inputs.line1,
        line2: inputs.line2,
        city: inputs.city,
        state: inputs.state,
        postal_code: inputs.postal_code,
    });
}

// Verifying *before* quoting is load-bearing for tax correctness, not a convenience. The verify
// request is what caches the EasyPost-standardized address that the quote below is keyed on
// (AjaxVerifyAddressController::cacheStandardizedAddress), so quoting first — or dropping the
// callback — silently reverts every US quote to the address the customer typed. There is no
// error and no failing unit or feature test when that happens; the order is only proven by
// tests/Browser/Checkout/GuestCheckoutStandardizedTaxTest.php.
function onAddressChanged() {
    verifyAddress(inputs, () => updateShippingOptions());
}

function onShippingRegionError(error: any) {
    shippingRegionErrors.city = "";
    shippingRegionErrors.postal_code = "";

    if (Object.prototype.hasOwnProperty.call(error.data.errors, "region")) {
        shippingRegionErrors[country.value.shipping_region_field] =
            error.data.errors.region[0];
        refs[country.value.shipping_region_field].value.focus();
    }
}

onMounted(() => {
    initializeAutocomplete(
        refs.line1.value,
        country.value.code,
        onPlaceChanged,
    );

    emitter.on("copyShippingAddressToBillingAddress", () =>
        copyShippingAddressFields(),
    );
    emitter.on("updateShippingOptions", () => updateShippingOptions());
    emitter.on("shippingRegionError", (error) => onShippingRegionError(error));

    switch (true) {
        case !!hasError(props.errors, "shipping_name"):
            return refs.name.value.focus();
        case !!hasError(props.errors, "shipping_line1"):
            return refs.line1.value.focus();
        case !!hasError(props.errors, "shipping_line2"):
            return refs.line2.value.focus();
        case !!hasError(props.errors, "shipping_city"):
            return refs.city.value.focus();
        case !!hasError(props.errors, "shipping_state"):
            return refs.state.value.focus();
        case !!hasError(props.errors, "shipping_postal_code"):
            return refs.postal_code.value.focus();
        case !!hasError(props.errors, "shipping_country_code"):
            return refs.country_code.value.focus();
        case !!hasError(props.errors, "shipping_phone"):
            return refs.phone.value.focus();
    }
});
</script>
